/**
 * Sort an array of objects in ascending order based on a specified property.
 * If the primary property values are the same, a secondary property is used.
 * @param array The array of objects to sort
 * @param primaryProp The primary property to sort by
 * @param secondaryProp The secondary property to sort by if the primary properties are the same
 * @example const sortedArray = sortByAsc(array, 'primaryProp', 'secondaryProp');
 */
function sortByAsc(array, primaryProp, secondaryProp) {
  return sortBy(array, primaryProp, secondaryProp);
}
/**
 * Sort an array of objects in descending order based on a specified property.
 * If the primary property values are the same, a secondary property is used.
 * @param array The array of objects to sort
 * @param primaryProp The primary property to sort by
 * @param secondaryProp The secondary property to sort by if the primary properties are the same
 * @example const sortedArray = sortByDesc(array, 'primaryProp', 'secondaryProp');
 */
function sortByDesc(array, primaryProp, secondaryProp) {
  return sortBy(array, primaryProp, secondaryProp, true);
}
/**
 * A type safe function for grouping an array of objects by a specified property. If there is no matching key, items will be grouped under "undefined"
 * @param array The array of objects to group
 * @param keyOrFunction The property to group by, or a function that returns the key
 * @example const grouped = groupBy(array, 'propertyToGroupBy');
 */
function groupBy(array, keyOrFunction) {
  return array.reduce((objectsByKeyValue, obj) => {
    const value = typeof keyOrFunction === 'function' ? keyOrFunction(obj) : obj[keyOrFunction];
    objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
    return objectsByKeyValue;
  }, {});
}
/**
 * Partitions the array into two sub-arrays based on condition
 * @param array The array of objects to partition
 * @param isValid The condition to partition by
 * @example const [pass, fail] = partition<IObject>(myArray, (item: IObject) => item.specialProp === 'Gandolf');
 */
function partitionArray(array, isValid) {
  return array.reduce(([pass, fail], elem) => {
    return isValid(elem) ? [[...pass, elem], fail] : [pass, [...fail, elem]];
  }, [[], []]);
}
/**
 * Index an array of objects by a specified property. If there are duplicate keys, the last object with that key will be used.
 * @param array The array of objects to index
 * @param getKey A function that returns the key to index by
 * @example const indexed = indexBy(productArray, (p: Product) => p.productId);
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function indexBy(array, getKey) {
  return array.reduce((running, current) => {
    const key = getKey(current);
    running[key] = current;
    return running;
  }, {});
}
/**
 * Internal methods
 */
function sortBy(array, primaryProp, secondaryProp, descending) {
  return [...array].sort((a, b) => {
    const primaryComparison = compareValues(a, b, primaryProp);
    if (primaryComparison !== 0) {
      return descending ? -primaryComparison : primaryComparison;
    }
    if (secondaryProp) {
      const secondaryComparison = compareValues(a, b, secondaryProp);
      return descending ? -secondaryComparison : secondaryComparison;
    }
    return 0; // If secondaryProp is not provided, maintain the original order.
  });
}
function compareValues(a, b, prop) {
  const comparison = String(a[prop]).localeCompare(String(b[prop]));
  return comparison < 0 ? -1 : comparison > 0 ? 1 : 0;
}

/**
 * Generated bundle index. Do not edit.
 */

export { groupBy, indexBy, partitionArray, sortByAsc, sortByDesc };
